import PageHeader from '@atlaskit/page-header';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { SendDatabaseCreatedViewEventData } from '@databases/analytics/src/types';
import {
  DatabaseSpotlight,
  VIEWS_CHANGEBOARDING_COORDINATION_MESSAGE_KEY,
} from '@databases/onboarding/DatabaseSpotlight';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { SkeletonItem } from '@databases/skeleton';
import { ViewId } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { getDatabaseFields } from '@databases/utils/database/utils';
import { createView } from '@databases/utils/database/view/view';
import { isEmbed } from '@databases/utils/utils';
import ViewSettingsButtons from '@databases/view-settings-buttons';
import ViewTabs from '@databases/view-tabs/ViewTabs';
import { messages as viewSettingsMessages } from '@databases/views/messages';
import { ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import { useViewUiState } from '@databases/views/useViewUiState';
import { getUniqueViewName } from '@databases/views/utils/viewName';
import afterFrame from 'afterframe';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import { useScope } from '../Scope';
import DatabaseActions, { DatabaseActionKey } from '../database/DatabaseActions';
import DatabaseNameInlineEdit from '../database-name/DatabaseNameInlineEdit';
import { ButtonGroup } from '../styled';

import ViewPicker from './view-picker/ViewPicker';
import DatabaseSearch from './view-settings-picker/DatabaseSearch';

export const Container = styled.div`
  display: flex;
  position: relative;
  overflow: hidden;
  align-items: center;
  padding-top: 10px;
  padding-bottom: 8px;
  column-gap: 100px;
  overflow-x: auto;
  margin-right: 8px;
  margin-left: 10px;

  > h2 {
    margin-right: 5px;
    min-width: 0;
  }

  > .expand {
    flex-grow: 1;
  }

  > :last-child {
    flex-shrink: 0;
  }
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  column-gap: 4px;
  overflow: auto;
  flex-shrink: 1;
  min-width: 250px;
  width: 100%;

  > div:first-child {
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
  }
`;

export interface ToolbarProps {
  contentId: string;
  actions?: DatabaseActionKey[];
  canEditContent: boolean;
  canEditFields: boolean;
  showViewSettings?: boolean;
}

/**
 * Renders a table that allows to edit database values using inline edit fields and to add fields and add entries to a
 * database.
 */
const Toolbar: FunctionComponent<ToolbarProps> = ({
  contentId,
  actions,
  canEditContent,
  canEditFields,
  showViewSettings = true,
}) => {
  const [viewEditingId, setViewEditingId] = useState('');
  const [isViewsMenuOpen, setIsViewsMenuOpen] = useState(false);

  const { view, setViewId, resolvedView, clearUnsavedView } = useView();

  const scope = useScope();
  const client = useAnalytics();
  const dispatch = defaultStoreUtils.useDispatch();
  const { setActivePanelSetting } = useViewUiState();
  const intl = useIntl();
  const { formatMessage } = intl;
  const { showPanel } = usePanel();

  const [savedViews, fields] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    return [databaseDraft.views, getDatabaseFields(databaseDraft)];
  });

  const openViewSettingsPanel = useCallback(
    (activeViewSetting: ViewPanelSettingType | null) => {
      const startedRenderingAt = performance.now();
      setActivePanelSetting(activeViewSetting);
      showPanel('ViewSettingsPanel', {
        autoFocusLastFilterValue: false,
        canEditViews: canEditFields,
        unsavedChangesMessage: formatMessage(
          viewSettingsMessages['view.exit-dialog-body']
        ),
      });

      afterFrame(() => {
        const reliability = ReliabilityMetrics.getInstance();
        reliability.successWithDuration(
          MetricName.DATABASE_PANEL_VIEW_SETTINGS_OPEN,
          startedRenderingAt,
          performance.now()
        );
      });

      // If table layout header cell selection is active,
      // remove selection border on view settings button click
      dispatch(deselectAll());
    },
    [setActivePanelSetting, showPanel, canEditFields, formatMessage, dispatch]
  );

  const handleAddView = useCallback(
    (source: SendDatabaseCreatedViewEventData['source']) => {
      let newView = createView(view.settings);

      newView = {
        ...newView,
        name: getUniqueViewName(
          newView,
          savedViews.map((v) => v.name),
          intl
        ),
      };

      dispatch(
        addChangeToDraft(contentId, scope, {
          type: DatabaseChangeType.ADD_SAVED_VIEW,
          view: newView,
        })
      );

      if (view.id === ViewId.UNSAVED) {
        clearUnsavedView();
      }

      setViewId(newView.id);

      openViewSettingsPanel(ViewPanelSettingType.OVERVIEW);

      client.sendDatabaseCreateViewEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        viewId: newView.id,
        source,
        layoutType: newView.settings.layout.type,
      });
    },
    [
      clearUnsavedView,
      client,
      contentId,
      dispatch,
      intl,
      openViewSettingsPanel,
      savedViews,
      setViewId,
      scope,
      view.id,
      view.settings,
    ]
  );

  return (
    <>
      {isEmbed() && (
        <PageHeader>
          <DatabaseNameInlineEdit contentId={contentId} isReadonly={!canEditContent} />
        </PageHeader>
      )}

      <Container>
        <DatabaseSpotlight
          coordinationMessageKey={VIEWS_CHANGEBOARDING_COORDINATION_MESSAGE_KEY}
          isEnabled={!isEmbed()}
        ></DatabaseSpotlight>
        <InnerContainer>
          <SkeletonItem isButton>
            <ViewTabs
              contentId={contentId}
              defaultView={resolvedView}
              isReadonly={!canEditFields}
              openViewSettingsPanel={openViewSettingsPanel}
              handleAddView={handleAddView}
              showViewSettings={showViewSettings}
              ViewPicker={ViewPicker}
              viewEditingId={viewEditingId}
              setViewEditingId={setViewEditingId}
              isViewsMenuOpen={isViewsMenuOpen}
              setIsViewsMenuOpen={setIsViewsMenuOpen}
            />
          </SkeletonItem>
        </InnerContainer>
        <ButtonGroup isCompact>
          {showViewSettings && (
            <DatabaseSearch
              contentId={contentId}
              value={view.settings}
              autoFocusLastFilterValue={false}
              showQuickSearch
            />
          )}

          <SkeletonItem isButton>
            <DatabaseActions
              contentId={contentId}
              placement="bottom-end"
              show={actions}
              isReadOnly={!canEditContent}
            />
          </SkeletonItem>
        </ButtonGroup>
      </Container>

      {showViewSettings && (
        <ViewSettingsButtons
          hasNoFields={!fields}
          openViewSettingsPanel={openViewSettingsPanel}
        />
      )}
    </>
  );
};

export default Toolbar;
